import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { Calendar, MapPin } from "lucide-react";

interface BlogPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post: {
    title: string;
    excerpt: string;
    content: string;
    featuredImage: string;
    publishDate: string;
    tags: string[];
  };
}

export const BlogPreview = ({ open, onOpenChange, post }: BlogPreviewProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-muted-foreground text-sm font-normal">
            Preview Mode
          </DialogTitle>
        </DialogHeader>

        <article className="prose prose-neutral dark:prose-invert max-w-none">
          {post.featuredImage && (
            <div className="relative aspect-video w-full overflow-hidden rounded-xl mb-6">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover m-0"
              />
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map((tag, i) => (
              <Badge key={i} variant="secondary">
                {tag}
              </Badge>
            ))}
          </div>

          {post.publishDate && (
            <p className="text-sm text-muted-foreground mb-2">
              {format(new Date(post.publishDate), "MMMM d, yyyy")}
            </p>
          )}

          <h1 className="text-3xl font-bold mb-4">{post.title || "Untitled Post"}</h1>

          {post.excerpt && (
            <p className="text-lg text-muted-foreground italic mb-6">
              {post.excerpt}
            </p>
          )}

          {post.content ? (
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />
          ) : (
            <p className="text-muted-foreground">No content yet...</p>
          )}
        </article>
      </DialogContent>
    </Dialog>
  );
};

interface GalleryPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gallery: {
    title: string;
    description: string;
    featuredImage: string;
    galleryType: string;
    eventDate: string;
    venue: string;
    images: { url: string }[];
    tags: string[];
  };
}

export const GalleryPreview = ({ open, onOpenChange, gallery }: GalleryPreviewProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-muted-foreground text-sm font-normal">
            Preview Mode
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {gallery.featuredImage && (
            <div className="relative aspect-video w-full overflow-hidden rounded-xl">
              <img
                src={gallery.featuredImage}
                alt={gallery.title}
                className="h-full w-full object-cover"
              />
            </div>
          )}

          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="capitalize">
                {gallery.galleryType}
              </Badge>
              {gallery.tags.map((tag, i) => (
                <Badge key={i} variant="secondary">
                  {tag}
                </Badge>
              ))}
            </div>

            <h1 className="text-3xl font-bold">{gallery.title || "Untitled Gallery"}</h1>

            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              {gallery.eventDate && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(gallery.eventDate), "MMMM d, yyyy")}
                </span>
              )}
              {gallery.venue && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {gallery.venue}
                </span>
              )}
            </div>

            {gallery.description && (
              <p className="text-muted-foreground">{gallery.description}</p>
            )}
          </div>

          {gallery.images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {gallery.images.map((img, index) => (
                <div key={index} className="aspect-square overflow-hidden rounded-lg">
                  <img
                    src={img.url}
                    alt={`Gallery image ${index + 1}`}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              No images added yet...
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
